/**
 * Protected Route Component with Automatic SSO Login
 * Instantly redirects to Entra ID SSO - no intermediate UI shown
 */
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import { logger } from '../utils/logger';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, login } = useAuth();
  const loginInitiated = useRef(false);
  const location = useLocation();

  // Automatically initiate SSO login when not authenticated - no UI shown
  useEffect(() => {
    if (!isLoading && !isAuthenticated && !loginInitiated.current) {
      loginInitiated.current = true;
      const targetPath = `${location.pathname}${location.search}${location.hash}`;
      sessionStorage.setItem('post_login_redirect', targetPath);
      logger.info('ProtectedRoute: Automatically initiating SSO login');
      // Instant redirect to Entra ID SSO
      login();
    }
  }, [isLoading, isAuthenticated, login, location.pathname, location.search, location.hash]);

  // Show minimal loading only during initial auth check
  // Once SSO redirect starts, show nothing (user will see Entra ID page)
  if (isLoading) {
    return (
      <div className="min-h-screen bg-base-100 flex items-center justify-center">
        <span className="loading loading-spinner loading-lg text-primary"></span>
      </div>
    );
  }

  // If not authenticated, return null - SSO redirect is happening
  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
